// Types for the new catalog-backed e-commerce endpoints
// (GET /e-commerce/catalog/deals, /catalog/categories/browse, etc.)
// Shape matches ProductService's EcomCatalogService responses — distinct from the
// legacy Product/QueryProductDto shape in Redux/ecommersProduct, which is still used
// by tabs this endpoint doesn't cover (Salar Aura, Refurbished).

export enum EcomDealType {
  COUPON = 'COUPON',
  DEAL = 'DEAL',
  PROMOTION = 'PROMOTION',
  BOGO = 'BOGO',
}

export interface CatalogBrandRef {
  _id: string;
  name: string;
  logo?: string;
}

export interface CatalogCategoryHierarchy {
  superCategory: string;
  category: string;
  subCategory: string;
  childCategory: string;
}

export interface CatalogAppliedOffer {
  type: 'DEAL' | 'COUPON' | 'PROMOTION' | 'BOGO';
  fundedBy: 'SELLER' | 'BRAND' | 'PLATFORM';
  amount: number;
}

export interface CatalogDealSnapshot {
  dealId: string;
  dealPrice: number;
  discountPercent: number;
  endDate: string;
  remainingUnits: number;
}

export interface CatalogCouponSnapshot {
  title: string;
  discountType: 'FLAT' | 'PERCENTAGE' | 'FREE_DELIVERY';
  discountValue: number;
}

export interface CatalogFeaturedOffer {
  sellerSkuId: string;
  sellerId: string;
  sellerName: string;
  finalPrice: number;
  unitPrice: number;
  discountPercent: number;
  availableQty: number;
  freeDelivery: boolean;
  fastDelivery: boolean;
  nextDayDelivery: boolean;
  sellerRating: number;
  computedAt: string;
  appliedOffers: CatalogAppliedOffer[];
  mrp: number;
  sellerPrice: number;
  promotionDiscount: number;
  promotion: { title: string; discountValue: number } | null;
  deal: CatalogDealSnapshot | null;
  displayPrice: number;
  couponDiscount: number;
  coupon: CatalogCouponSnapshot | null;
  savingsAmount: number;
  savingsPercent: number;
}

export interface CatalogBogoOffer {
  bogoId: string;
  title: string;
  detailPageDisplayText?: string;
  benefitQuantity: number;
}

export interface CatalogProduct {
  _id: string;
  ssin: string;
  name: string;
  description?: string;
  mainImage: string;
  brand: CatalogBrandRef | null;
  categoryHierarchy: CatalogCategoryHierarchy;
  condition: 'new' | 'refurbished';
  variantCount: number;
  sellerCount: number;
  stockStatus: string;
  featuredOffer: CatalogFeaturedOffer;
  reviews: { averageRating: number; totalReviews: number } | null;
  bogoOffers: CatalogBogoOffer[];
}

export interface CatalogBreadcrumbItem {
  level: string;
  name: string;
}

export interface CatalogChild {
  name: string;
  count: number;
}

// Numbered-page pagination — returned when `page` is passed instead of `cursor`.
export interface CatalogNumberedPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface CatalogBrandFacet {
  _id: string;
  count: number;
  name: string;
  logo?: string;
  selected: boolean;
}

export interface CatalogPriceRangeFacet {
  min: number;
  max: number;
  applied: { min: number | null; max: number | null };
}

export interface CatalogAttributeOption {
  value: string;
  count: number;
  selected: boolean;
}

export interface CatalogDeliveryFacet {
  count: number;
  selected: boolean;
}

export interface CatalogDiscountBucket {
  label: string;
  minDiscount: number;
  count: number;
  selected: boolean;
}

export interface CatalogRatingBucket {
  label: string;
  minRating: number;
  count: number;
  selected: boolean;
}

export interface CatalogFilters {
  brands: CatalogBrandFacet[];
  priceRange: CatalogPriceRangeFacet;
  attributes: Record<string, CatalogAttributeOption[]>;
  delivery: {
    freeDelivery: CatalogDeliveryFacet;
    fastDelivery: CatalogDeliveryFacet;
    nextDayDelivery: CatalogDeliveryFacet;
  };
  discountBuckets: CatalogDiscountBucket[];
  ratingBuckets: CatalogRatingBucket[];
  condition: CatalogAttributeOption[];
}

export interface CatalogBrowseData {
  breadcrumb: CatalogBreadcrumbItem[];
  children: CatalogChild[];
  products: CatalogProduct[];
  pagination: CatalogNumberedPagination;
  filters: CatalogFilters;
}

export interface CatalogApiResponse<T> {
  statusCode: number;
  data: T;
}

// Query params for GET /e-commerce/catalog/deals.
// Always pass `page` (not `cursor`) so the API returns numbered pagination,
// which is what the Deals page's page-number UI needs.
export interface EcomDealsQueryParams {
  page?: number;
  limit?: number;
  q?: string;
  superCategory?: string;
  category?: string;
  subCategory?: string;
  childCategory?: string;
  brandId?: string[];
  minPrice?: number;
  maxPrice?: number;
  minDiscount?: number;
  minRating?: number;
  condition?: 'new' | 'refurbished';
  dealType?: EcomDealType;
  // Accepted by EcomDealsQueryDto (extends EcomProductQueryDto) on the backend.
  sort?: 'relevance' | 'price_asc' | 'price_desc' | 'newest' | 'rating' | 'discount' | 'ending_soon';
  freeDelivery?: 'true' | 'false';
  fastDelivery?: 'true' | 'false';
  nextDayDelivery?: 'true' | 'false';
  withFilters?: 'true' | 'false';
}

// Cursor pagination — returned when `cursor` is passed instead of `page`.
// Used for infinite scroll on the category listing page.
export interface CatalogCursorPagination {
  nextCursor: string | null;
  hasMore: boolean;
  limit: number;
  count: number;
  total?: number;
  from?: number;
}

// Browse response — API returns either pagination shape based on whether
// `page` or `cursor` was sent. Use a union so the slice can normalize.
export interface CatalogBrowseResponse {
  breadcrumb: CatalogBreadcrumbItem[];
  children: CatalogChild[];
  products: CatalogProduct[];
  pagination: CatalogNumberedPagination | CatalogCursorPagination;
  filters: CatalogFilters | null;
}

// Category tree node — from GET /e-commerce/catalog/categories/tree.
// Nesting mirrors superCategory → category → subCategory → childCategory.
export interface CatalogTreeNode {
  name: string;
  count: number;
  categories?: CatalogTreeNode[];
  subCategories?: CatalogTreeNode[];
  childCategories?: CatalogTreeNode[];
}

// Query params for GET /e-commerce/catalog/categories/browse (cursor-based).
// Mirrors EcomProductQueryDto from the backend so the frontend can pass
// filters/sort directly to the API without translation.
export interface EcomBrowseQueryParams {
  cursor?: string;
  page?: number;
  limit?: number;
  q?: string;
  superCategory?: string;
  category?: string;
  subCategory?: string;
  childCategory?: string;
  brandId?: string[];
  minPrice?: number;
  maxPrice?: number;
  minDiscount?: number;
  attributes?: Record<string, string | string[]>;
  inStock?: string;
  freeDelivery?: string;
  fastDelivery?: string;
  nextDayDelivery?: string;
  minRating?: number;
  condition?: string;
  sort?: string;
  withFilters?: string;
}

export interface EcomCatalogState {
  deals: {
    breadcrumb: CatalogBreadcrumbItem[];
    children: CatalogChild[];
    products: CatalogProduct[];
    pagination: CatalogNumberedPagination;
    filters: CatalogFilters | null;
  };
  browse: {
    breadcrumb: CatalogBreadcrumbItem[];
    children: CatalogChild[];
    products: CatalogProduct[];
    pagination: CatalogCursorPagination;
    filters: CatalogFilters | null;
  };
  tree: CatalogTreeNode[];
  dealsRail: CatalogProduct[];
  loading: boolean;
  browseLoading: boolean;
  loadingMore: boolean;
  treeLoading: boolean;
  dealsRailLoading: boolean;
  error: string | null;
  browseError: string | null;
}
